import * as Api from '@ViewModels';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAdminAccountQuery } from '../../../../queries';
import { SuccessCheckIcon } from '../../svgs/icons/SuccessCheckIcon';

export const useAutomationCardData = (
	automationTemplate: Api.IAutomationTemplateReference,
	impersonationContext?: Api.IImpersonationContext
) => {
	const isImpersonating = Boolean(impersonationContext);
	const userSession = useUserSession();
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext?.account?.id,
		enabled: impersonationContext?.account?.id !== undefined,
	});
	let automationName: string = automationTemplate?.status;
	let statusIcon: React.ReactNode = null;
	const trigger = automationTemplate?.draftTrigger || automationTemplate?.publishedTrigger;
	// When an admin is impersonating, resolve the trigger title against the impersonated
	// account's resource selector settings, not the admin's own session. Otherwise labels
	// for account-specific selectors (e.g. TurningXX) render with the admin's stale data.
	// See Real-Magic/Levitate#18344. Mirrors the pattern in AutomationTriggerCard.
	let automationTriggerTitle = '';
	if (!impersonationContext?.account?.id) {
		automationTriggerTitle = Api.VmUtils.Automations.triggers.getAutomationTriggerTitle(trigger, userSession.account);
	} else if (accountQuery.data) {
		automationTriggerTitle = Api.VmUtils.Automations.triggers.getAutomationTriggerTitle(trigger, accountQuery.data);
	}
	const triggerDescription = (trigger as Api.ITagAutomationTrigger)?.tag || automationTriggerTitle;

	const inProgress = automationTemplate?.stats?.inProgress ?? 0;
	const completed = automationTemplate?.stats?.completed ?? 0;

	const inProgressContactString = `${inProgress} contact${inProgress > 1 || inProgress === 0 ? 's' : ''}`;
	const completedContactString = `${completed} contact${completed > 1 || completed === 0 ? 's' : ''}`;

	const showCompletedButton = completed > 0 && !isImpersonating;

	const showInProgressButton = inProgress > 0 && !isImpersonating;

	const automationId = automationTemplate?.id;

	switch (automationName) {
		case Api.TemplateStatus.Published: {
			statusIcon = <SuccessCheckIcon />;
			automationName = 'Enabled';
			break;
		}
		default: {
			break;
		}
	}

	return {
		automationId,
		automationName,
		completedContactString,
		inProgressContactString,
		showCompletedButton,
		showInProgressButton,
		statusIcon,
		trigger,
		triggerDescription,
	};
};
